import React, { useState, useEffect } from 'react';
import {
  Shield, Users, Coins, Search, ArrowUp, ArrowDown,
  RefreshCw, PlusCircle, UserCheck, AlertTriangle, BarChart3
} from 'lucide-react';
import { User, TokenTransaction } from '../types';
import {
  getAllUsersWithTokenInfo,
  addTokens,
  TokenTransactionType,
  getUserTokenTransactions
} from '../services/tokenService';
import AdminReports from './AdminReports';
import { useLanguage } from '../contexts/LanguageContext';

interface AdminPanelProps {
  user: User;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ user }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'users' | 'reports'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'displayName' | 'email' | 'tokens'>('tokens');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [tokensToAdd, setTokensToAdd] = useState(10);
  const [tokensDescription, setTokensDescription] = useState(t('admin.addTokensForm.defaultDescription'));
  const [processingUser, setProcessingUser] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [userTransactions, setUserTransactions] = useState<TokenTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Verificar si el usuario actual es administrador
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Verificar si el usuario actual es administrador
    const checkAdmin = () => {
      if (user && (user.isAdmin || user.email === 'autotrindemexico@gmail.com')) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
        setError(t('admin.accessDenied.message'));
      }
    };

    checkAdmin();
  }, [user]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!isAdmin) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const allUsers = await getAllUsersWithTokenInfo();
        setUsers(allUsers);
      } catch (err: any) {
        console.error('Error al cargar usuarios:', err);
        setError(err.message || t('admin.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const handleRefresh = async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const allUsers = await getAllUsersWithTokenInfo();
      setUsers(allUsers);
    } catch (err: any) {
      console.error('Error al actualizar usuarios:', err);
      setError(err.message || t('admin.errorUpdating'));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: 'displayName' | 'email' | 'tokens') => {
    if (sortField === field) {
      // Cambiar dirección si es el mismo campo
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nuevo campo, establecer orden predeterminado
      setSortField(field);
      setSortDirection(field === 'tokens' ? 'desc' : 'asc');
    }
  };

  const handleAddTokens = async () => {
    if (!selectedUser || !isAdmin) return;
    
    setProcessingUser(selectedUser);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const success = await addTokens(
        selectedUser,
        tokensToAdd,
        TokenTransactionType.ADDED,
        tokensDescription,
        user.uid
      );
      
      if (success) {
        // Actualizar la lista de usuarios
        const updatedUsers = users.map(u => {
          if (u.uid === selectedUser) {
            return {
              ...u,
              tokens: (u.tokens || 0) + tokensToAdd
            };
          }
          return u;
        });
        
        setUsers(updatedUsers);
        setSuccessMessage(t('admin.addTokensForm.success', { count: tokensToAdd }));
        setShowTokenForm(false);
        
        // Recargar las transacciones del usuario
        await loadUserTransactions(selectedUser);
      } else {
        setError(t('admin.addTokensForm.error'));
      }
    } catch (err: any) {
      console.error('Error al añadir tokens:', err);
      setError(err.message || 'Error al añadir tokens');
    } finally {
      setProcessingUser(null);
    }
  };

  const loadUserTransactions = async (userId: string) => {
    setLoadingTransactions(true);
    
    try {
      const transactions = await getUserTokenTransactions(userId, 50);
      setUserTransactions(transactions);
    } catch (err) {
      console.error('Error al cargar transacciones:', err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleUserSelect = async (userId: string) => {
    if (selectedUser === userId) {
      // Deseleccionar usuario si ya está seleccionado
      setSelectedUser(null);
      setUserTransactions([]);
    } else {
      setSelectedUser(userId);
      await loadUserTransactions(userId);
    }
  };

  // Filtrar y ordenar usuarios
  const filteredUsers = users
    .filter(user => {
      if (!searchQuery) return true;
      
      const query = searchQuery.toLowerCase();
      return (
        (user.displayName && user.displayName.toLowerCase().includes(query)) ||
        (user.email && user.email.toLowerCase().includes(query))
      );
    })
    .sort((a, b) => {
      if (sortField === 'tokens') {
        const tokensA = a.tokens || 0;
        const tokensB = b.tokens || 0;
        return sortDirection === 'asc' ? tokensA - tokensB : tokensB - tokensA;
      } else {
        const valueA = a[sortField]?.toLowerCase() || '';
        const valueB = b[sortField]?.toLowerCase() || '';
        return sortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
    });

  // Formatear fecha
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (!isAdmin) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">{t('admin.accessDenied.title')}</h3>
        <p className="text-red-700">
          {t('admin.accessDenied.message')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="p-4 pb-6 bg-primary text-white shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold flex items-center">
            <Shield className="mr-2" />
            {t('admin.title')}
          </h2>

          {activeTab === 'users' && (
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 rounded-full hover:bg-primary-dark transition-colors"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          )}
        </div>

        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'users'
                ? 'bg-white text-primary font-semibold'
                : 'bg-primary-dark hover:bg-opacity-80 text-white'
            }`}
          >
            <Users size={18} className="mr-2" />
            {t('admin.usersTab')}
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'reports'
                ? 'bg-white text-primary font-semibold'
                : 'bg-primary-dark hover:bg-opacity-80 text-white'
            }`}
          >
            <BarChart3 size={18} className="mr-2" />
            {t('admin.reportsTab')}
          </button>
        </div>
      </div>

      {activeTab === 'reports' ? (
        <div className="pt-8">
          <AdminReports />
        </div>
      ) : (
        <React.Fragment>
      
      <div className="p-4">
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.search')}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          
          <div className="flex items-center text-sm text-gray-600">
            <div className="flex items-center">
              <Users className="h-4 w-4 mr-1" />
              <span>{t('admin.total')} {users.length} {t('admin.users')}</span>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600">
            {successMessage}
          </div>
        )}
        
        {loading ? (
          <div className="p-8 flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    <button
                      onClick={() => handleSort('displayName')}
                      className="flex items-center focus:outline-none"
                    >
                      {t('admin.table.name')}
                      {sortField === 'displayName' && (
                        sortDirection === 'asc' ?
                          <ArrowUp size={14} className="ml-1" /> :
                          <ArrowDown size={14} className="ml-1" />
                      )}
                    </button>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    <button
                      onClick={() => handleSort('email')}
                      className="flex items-center focus:outline-none"
                    >
                      {t('admin.table.email')}
                      {sortField === 'email' && (
                        sortDirection === 'asc' ?
                          <ArrowUp size={14} className="ml-1" /> :
                          <ArrowDown size={14} className="ml-1" />
                      )}
                    </button>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    <button
                      onClick={() => handleSort('tokens')}
                      className="flex items-center focus:outline-none"
                    >
                      {t('admin.table.tokens')}
                      {sortField === 'tokens' && (
                        sortDirection === 'asc' ?
                          <ArrowUp size={14} className="ml-1" /> :
                          <ArrowDown size={14} className="ml-1" />
                      )}
                    </button>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('admin.table.status')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('admin.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                      {t('admin.noUsersFound')}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((userItem) => (
                    <tr 
                      key={userItem.uid} 
                      className={`
                        hover:bg-gray-50 cursor-pointer transition-colors
                        ${selectedUser === userItem.uid ? 'bg-blue-50' : ''}
                      `}
                      onClick={() => handleUserSelect(userItem.uid)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 bg-primary text-white rounded-full flex items-center justify-center">
                            {userItem.displayName?.[0].toUpperCase() || userItem.email?.[0].toUpperCase() || 'U'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {userItem.displayName || 'Usuario sin nombre'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {userItem.isAdmin ? t('admin.status.administrator') : t('admin.status.user')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {userItem.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Coins size={16} className="text-primary mr-2" />
                          <span className="text-sm font-medium">
                            {userItem.isAdmin ? t('admin.status.unlimited') : userItem.tokens || 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {userItem.isAdmin ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                            Admin
                          </span>
                        ) : userItem.tokens === 0 ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            {t('admin.status.noTokens')}
                          </span>
                        ) : userItem.tokens !== undefined && userItem.tokens <= 3 ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            {t('admin.status.lowTokens')}
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            {t('admin.status.active')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUser(userItem.uid);
                            setShowTokenForm(true);
                          }}
                          disabled={userItem.isAdmin || processingUser === userItem.uid}
                          className={`
                            inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded
                            ${userItem.isAdmin 
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                              : 'bg-primary text-white hover:bg-primary-dark'}
                          `}
                        >
                          <PlusCircle size={14} className="mr-1" />
                          {t('admin.addTokens')}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Sección de detalle de usuario seleccionado */}
        {selectedUser && (
          <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 p-4 border-b border-gray-200">
              <h3 className="font-medium flex items-center">
                <UserCheck size={18} className="text-primary mr-2" />
                {t('admin.userDetail')}
              </h3>
            </div>
            
            {/* Formulario para añadir tokens */}
            {showTokenForm && (
              <div className="p-4 bg-blue-50 border-b border-blue-100">
                <h4 className="font-medium text-blue-800 mb-3">{t('admin.addTokensForm.title')}</h4>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.addTokensForm.amount')}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={tokensToAdd}
                      onChange={(e) => setTokensToAdd(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.addTokensForm.description')}
                    </label>
                    <input
                      type="text"
                      value={tokensDescription}
                      onChange={(e) => setTokensDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowTokenForm(false)}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    {t('admin.addTokensForm.cancel')}
                  </button>
                  
                  <button
                    onClick={handleAddTokens}
                    disabled={processingUser === selectedUser}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark flex items-center"
                  >
                    {processingUser === selectedUser ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('admin.addTokensForm.processing')}
                      </>
                    ) : (
                      <>
                        <PlusCircle size={16} className="mr-2" />
                        {t('admin.addTokensForm.addCredits')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            
            {/* Historial de transacciones */}
            <div className="p-4">
              <h4 className="font-medium text-gray-800 mb-3">{t('admin.transactionHistory')}</h4>
              
              {loadingTransactions ? (
                <div className="p-8 flex justify-center items-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : userTransactions.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  {t('admin.noTransactions')}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('admin.transactionTable.date')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('admin.transactionTable.type')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('admin.transactionTable.amount')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('admin.transactionTable.description')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userTransactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(transaction.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              transaction.type === 'used' 
                                ? 'bg-red-100 text-red-800' 
                                : transaction.type === 'purchased'
                                  ? 'bg-purple-100 text-purple-800'
                                  : transaction.type === 'initial'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-green-100 text-green-800'
                            }`}>
                              {transaction.type === 'used' ? t('admin.transactionTypes.used') :
                               transaction.type === 'purchased' ? t('admin.transactionTypes.purchased') :
                               transaction.type === 'initial' ? t('admin.transactionTypes.initial') : t('admin.transactionTypes.added')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={transaction.amount < 0 ? 'text-red-600' : 'text-green-600'}>
                              {transaction.amount > 0 ? `+${transaction.amount}` : transaction.amount}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {transaction.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default AdminPanel;